// =============================================
// NsConfirmDialog — JEDYNA brama przed zmiana nameserverow (AfterMarket ns/set).
// Zly NS = domena offline, dlatego: liczba domen w tytule, lista old → new,
// linijka "pominiete: ..." i wyrazne ostrzezenie. Uzywany przez kreator
// (N domen) i akcje inline w "Domeny w CF" (1 domena). Gdy nsRewriteCount = 0
// (nic nie przepisujemy, tylko zony / rekordy A) → przycisk "Wykonaj" bez
// ostrzezenia o NS.
// =============================================
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, ArrowRight } from "lucide-react";
import type { NsSkipCategory } from "@/services/cloudflare/transferTypes";

export interface NsConfirmRow {
  domain: string;
  currentNs: string[];
  /** docelowe NS; "z zony" gdy zona dopiero powstanie i NS nie sa jeszcze znane */
  newNs: string[] | "FROM_CREATED_ZONE";
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onConfirm: () => void;
  rows: NsConfirmRow[];
  /** pominiete ns_set per kategoria (kreator); brak = nie pokazuj linijki */
  skipSummary?: Partial<Record<NsSkipCategory, number>>;
  /** co jeszcze sie wykona poza NS, np. "2 zony, 3 rekordy A" */
  otherActionsLabel?: string;
  confirming?: boolean;
}

const SKIP_LABEL: Record<NsSkipCategory, string> = {
  already_cf: "już na CF",
  not_on_am: "brak na AfterMarket",
  no_zone: "brak zony",
  other: "inne",
};

export const NsConfirmDialog = ({ open, onOpenChange, onConfirm, rows, skipSummary, otherActionsLabel, confirming }: Props) => {
  const n = rows.length;
  const skipLine = skipSummary
    ? (Object.keys(SKIP_LABEL) as NsSkipCategory[])
      .filter((k) => (skipSummary[k] ?? 0) > 0)
      .map((k) => `${skipSummary[k]} ${SKIP_LABEL[k]}`)
      .join(" · ")
    : "";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {n > 0 ? (
              <><AlertTriangle className="h-5 w-5 text-amber-400" />Zmienisz NS dla {n} {n === 1 ? "domeny" : "domen"}</>
            ) : (
              <>Wykonać plan?</>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 pt-2 text-sm">
              {otherActionsLabel && <p className="text-muted-foreground">Zostaną wykonane: {otherActionsLabel}.</p>}
              {skipLine && <p className="text-xs text-muted-foreground">Pominięte NS: {skipLine}.</p>}
              {n > 0 && (
                <div className="max-h-72 overflow-y-auto rounded-lg border divide-y">
                  {rows.map((r) => (
                    <div key={r.domain} className="px-3 py-2 text-xs">
                      <div className="font-mono font-medium mb-1">{r.domain}</div>
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-muted-foreground mb-0.5">Obecne</div>
                          {(r.currentNs.length ? r.currentNs : ["—"]).map((x) => <div key={x} className="font-mono truncate">{x}</div>)}
                        </div>
                        <ArrowRight className="h-4 w-4 mt-4 text-muted-foreground shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-muted-foreground mb-0.5">Nowe</div>
                          {r.newNs === "FROM_CREATED_ZONE"
                            ? <div className="font-mono text-emerald-400">NS z nowo utworzonej zony</div>
                            : r.newNs.map((x) => <div key={x} className="font-mono text-emerald-400 truncate">{x}</div>)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {n > 0 && <p className="text-amber-400">Błędne NS mogą wyłączyć domenę do czasu poprawy. Zmiany idą po kolei, domena po domenie. Kontynuować?</p>}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={confirming}>Anuluj</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={confirming}>
            {n > 0 ? "Zmień nameservery" : "Wykonaj"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
